import { useCallback, useMemo, useRef, useState } from "react";
import { useTypedSearchParams } from "react-router-typesafe-routes";

import { useDisplayLocale } from "~/common/functions/useDisplayLocale";
import { useCategoryList, useCategoryTab } from "~/features/admin/categories/hooks";
import { toCategoryRowList } from "~/features/admin/categories/utils/transformers";
import { v2Route } from "~/v2/routes";
import { CategoriesPresenter } from "./categories.presenter";

const DEFAULT_PAGE_SIZE = 25;

export function CategoriesContainer() {
  const locale = useDisplayLocale();
  const [activeTab, setActiveTab] = useCategoryTab();
  const [params, setParams] = useTypedSearchParams(v2Route.admin.categories);
  const { data, refetch } = useCategoryList();

  const rows = useMemo(() => toCategoryRowList(data.categories, locale), [data.categories, locale]);

  // The text input is driven by local state for instant feedback. The URL is only a
  // background mirror (sharable / restored on reload + back-forward): React Router v7 applies
  // navigation updates in a transition, so binding the input's value straight to `params.search`
  // makes fast typing lag. We adopt the URL value into local state only on a *genuine external*
  // change (back/forward, shared link): the URL changed since we last saw it AND it differs from
  // what we have locally. Our own writes — which the URL only lags toward — match local, so they
  // never clobber the in-progress value.
  const urlSearch = params.search ?? "";
  const [search, setSearch] = useState(urlSearch);
  const lastUrlSearchRef = useRef(urlSearch);
  if (urlSearch !== lastUrlSearchRef.current) {
    lastUrlSearchRef.current = urlSearch;
    if (urlSearch !== search) setSearch(urlSearch);
  }

  const typeId = params.typeId ?? null;
  const excludeDeactivated = params.excludeDeactivated ?? true;
  const pageIndex = params.page ?? 0;
  const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
  const selectedCategory = params.selectedCategory ?? null;

  const onSearchChange = useCallback(
    (next: string) => {
      setSearch(next);
      setParams((prev) => ({ ...prev, search: next ? next : undefined, page: undefined }));
    },
    [setParams],
  );

  const onTypeIdChange = useCallback(
    (next: "entity" | "relation" | null) => {
      setParams((prev) => ({ ...prev, typeId: next ?? undefined, page: undefined }));
    },
    [setParams],
  );

  const onExcludeDeactivatedChange = useCallback(
    (next: boolean) => {
      setParams((prev) => ({ ...prev, excludeDeactivated: next ? undefined : false, page: undefined }));
    },
    [setParams],
  );

  const onPageIndexChange = useCallback(
    (next: number) => {
      setParams((prev) => ({ ...prev, page: next === 0 ? undefined : next }));
    },
    [setParams],
  );

  const onPageSizeChange = useCallback(
    (next: number) => {
      setParams((prev) => ({
        ...prev,
        pageSize: next === DEFAULT_PAGE_SIZE ? undefined : next,
        page: undefined,
      }));
    },
    [setParams],
  );

  const onRefresh = useCallback(() => {
    void refetch();
  }, [refetch]);

  const onSelect = useCallback((id: string) => setParams((prev) => ({ ...prev, selectedCategory: id })), [setParams]);

  const onCloseSelected = useCallback(
    () => setParams((prev) => ({ ...prev, selectedCategory: undefined })),
    [setParams],
  );

  // Prev / next in the Peek panel walk the visible (excludeDeactivated-filtered) rows.
  // Tree / hierarchy tabs use their own ordering, so prev/next is only meaningful on the
  // list tab — undefined on the others to hide the controls.
  const peekRows = useMemo(
    () => (excludeDeactivated ? rows.filter((r) => !r.isDeactivated) : rows),
    [rows, excludeDeactivated],
  );
  const onPeekPrev = useMemo(() => {
    if (activeTab !== "categories" || !selectedCategory) return undefined;
    const idx = peekRows.findIndex((r) => r.id === selectedCategory);
    if (idx <= 0) return null;
    const prevId = peekRows[idx - 1].id;
    return () => setParams((prev) => ({ ...prev, selectedCategory: prevId }));
  }, [activeTab, selectedCategory, peekRows, setParams]);
  const onPeekNext = useMemo(() => {
    if (activeTab !== "categories" || !selectedCategory) return undefined;
    const idx = peekRows.findIndex((r) => r.id === selectedCategory);
    if (idx < 0 || idx >= peekRows.length - 1) return null;
    const nextId = peekRows[idx + 1].id;
    return () => setParams((prev) => ({ ...prev, selectedCategory: nextId }));
  }, [activeTab, selectedCategory, peekRows, setParams]);

  return (
    <CategoriesPresenter
      activeTab={activeTab}
      onActiveTabChange={setActiveTab}
      rows={rows}
      onSelect={onSelect}
      search={search}
      onSearchChange={onSearchChange}
      typeId={typeId}
      onTypeIdChange={onTypeIdChange}
      excludeDeactivated={excludeDeactivated}
      onExcludeDeactivatedChange={onExcludeDeactivatedChange}
      pageIndex={pageIndex}
      onPageIndexChange={onPageIndexChange}
      pageSize={pageSize}
      onPageSizeChange={onPageSizeChange}
      onRefresh={onRefresh}
      selectedCategory={selectedCategory}
      onCloseSelected={onCloseSelected}
      onPeekPrev={onPeekPrev}
      onPeekNext={onPeekNext}
    />
  );
}
